import { LECTERN, footprintDistance, type Footprint, type Point } from '@vrlearning/lesson-schema';
import { TEST_PANEL, testPanelAt, testPanelHeight } from './test-panel.js';

/**
 * Where the test panel hangs so it never covers the item or the reference figure (ADR-048; review of
 * Track C). It hangs in the lectern's own plane, so it swings and tilts with it; this chooses which side
 * of the lectern's panel, how far out, or above it, by looking at the footprints of what stands in the
 * world. Pure: the lectern's pose in the world in, the place in the lectern's plane out.
 */
export type WorldPose = {
  /** Metres, world. */
  position: readonly [number, number, number];
  /** About y, radians, as three.js turns it (rotation order YXZ). */
  yaw: number;
  /** About x, radians. */
  tilt: number;
};

export type PanelPlace = { x: number; y: number; side: 1 | -1; kind: 'beside' | 'above' };

/** Air kept between the panel and what it must not touch. */
const AIR = 0.05;
/** The figure's radius (a capsule 0.17 m wide, a head 0.12) and the air round it. */
export const FIGURE_REACH = 0.3;
const ASIDE = [0, 0.35, 0.7, 1.05];

/** A point of the lectern's plane in the world (the same maths as lesson-schema's framing). */
export function worldPoint(pose: WorldPose, x: number, y: number): [number, number, number] {
  const ty = y * Math.cos(pose.tilt);
  const tz = y * Math.sin(pose.tilt);
  const c = Math.cos(pose.yaw);
  const s = Math.sin(pose.yaw);
  return [
    pose.position[0] + x * c + tz * s,
    pose.position[1] + ty,
    pose.position[2] - x * s + tz * c,
  ];
}

export type Obstacles = {
  /** The item's footprint on the ground (modelFootprint). */
  readonly item?: Footprint | undefined;
  /** Where the figure stands. */
  readonly figure?: readonly [number, number, number] | undefined;
};

/** The panel's horizontal footprint, as points sampled over it: a grid across its rectangle. */
export function panelPoints(pose: WorldPose, at: { x: number; y: number }, rows: number): Point[] {
  const height = testPanelHeight(rows);
  const top = at.y + TEST_PANEL.rowHeight / 2;
  const points: Point[] = [];
  const across = 8;
  const down = Math.max(2, rows * 2);
  for (let i = 0; i <= across; i++) {
    for (let j = 0; j <= down; j++) {
      const x = at.x - TEST_PANEL.width / 2 + (TEST_PANEL.width * i) / across;
      const y = top - (height * j) / down;
      const world = worldPoint(pose, x, y);
      points.push([world[0], world[2]]);
    }
  }
  return points;
}

/** How many of the panel's sample points are in the way (0: clear). */
export function collisions(
  pose: WorldPose,
  at: { x: number; y: number },
  rows: number,
  obstacles: Obstacles,
): number {
  let hits = 0;
  for (const point of panelPoints(pose, at, rows)) {
    if (obstacles.item && footprintDistance(obstacles.item, point) < AIR) hits++;
    else if (
      obstacles.figure &&
      Math.hypot(point[0] - obstacles.figure[0], point[1] - obstacles.figure[2]) < FIGURE_REACH
    ) {
      hits++;
    }
  }
  return hits;
}

/** The item's side of the lectern, in its own plane: 1 when the item is to its right. */
function subjectSide(pose: WorldPose, obstacles: Obstacles): 1 | -1 {
  const target: Point | undefined = obstacles.item
    ? centreOf(obstacles.item)
    : obstacles.figure
      ? [obstacles.figure[0], obstacles.figure[2]]
      : undefined;
  if (!target) return 1;
  const dx = target[0] - pose.position[0];
  const dz = target[1] - pose.position[2];
  // The lectern's own x axis in the world is (cos yaw, −sin yaw).
  const local = dx * Math.cos(pose.yaw) - dz * Math.sin(pose.yaw);
  return local > 0 ? 1 : -1;
}

function centreOf(footprint: Footprint): Point {
  const sum = footprint.reduce<Point>((acc, p) => [acc[0] + p[0], acc[1] + p[1]], [0, 0]);
  return [sum[0] / footprint.length, sum[1] / footprint.length];
}

/**
 * The first place, in order, that touches nothing: the side away from the item at the lectern's edge,
 * then further out, then the other side the same, then above the lectern's panel. The place held before is
 * kept while it stays clear and on the side away from the item (the lectern slides for a few frames: the
 * panel does not flicker); if nothing is clear, the one that touches least.
 */
export function placeTestPanel(input: {
  pose: WorldPose;
  rows: number;
  obstacles: Obstacles;
  previous?: PanelPlace | undefined;
}): PanelPlace {
  const { pose, rows, obstacles } = input;
  const away = subjectSide(pose, obstacles) === 1 ? -1 : 1;
  const candidates: PanelPlace[] = [];
  for (const side of [away, away === 1 ? -1 : 1] as const) {
    for (const aside of ASIDE) {
      candidates.push({ ...testPanelAt(side, aside), side, kind: 'beside' });
    }
  }
  const above =
    LECTERN.panelY +
    LECTERN.height / 2 +
    AIR +
    TEST_PANEL.rowHeight / 2 +
    testPanelHeight(rows) -
    TEST_PANEL.rowHeight;
  candidates.push({ x: 0, y: above, side: away, kind: 'above' });

  const prev = input.previous;
  if (prev && prev.side === away && collisions(pose, prev, rows, obstacles) === 0) return prev;
  let best = candidates[0]!;
  let fewest = Infinity;
  for (const candidate of candidates) {
    const hits = collisions(pose, candidate, rows, obstacles);
    if (hits === 0) return candidate;
    if (hits < fewest) {
      fewest = hits;
      best = candidate;
    }
  }
  return best;
}
